import React, { useEffect, useCallback, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import classnames from "classnames";
import Button from "../Button";
import Icon from "../Icon";
import useFocusTrap from "../../hooks/useFocusTrapping";

import "./modal.scss";

const Modal = ({
  variation,
  iconName,
  iconTheme,
  heading,
  subtitle,
  suggestions,
  className,
  children,
  show,
  cancelButton,
  confirmButton,
  onClose,
  onConfirm,
}) => {
  const focusTrapRef = useFocusTrap(show);
  const [focusElement, setFocusElement] = useState(null);
  const keyDownHandler = useCallback(
    ({ code }) => {
      if (code === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (show) {
      setFocusElement(document.activeElement);
      document.body.classList.add("scroll-disabled");
      document.addEventListener("keydown", keyDownHandler);
    } else {
      document.body.classList.remove("scroll-disabled");
      focusElement?.focus();
      setFocusElement(null);
    }
    return () => document.removeEventListener("keydown", keyDownHandler);
  }, [keyDownHandler, show, focusElement]);

  return ReactDOM.createPortal(
    <>
      {show && (
        <div
          className={classnames("modal", className, {
            [`modal--${variation}`]: variation,
          })}
          role="dialog"
          aria-labelledby="modal-dialog-heading"
          ref={focusTrapRef}
        >
          <div className="modal__background" />
          <div className="modal__wrapper">
            <div className="modal__container">
              <Button
                className="modal__close"
                variant="outline"
                iconName="system-close"
                iconSize="small"
                aria-label="Close Modal"
                onClick={onClose}
              />
              {iconName && (
                <div className="modal__icon">
                  <Icon name={iconName} theme={iconTheme} size="extra-large" />
                </div>
              )}
              <h2 className="modal__heading" id="modal-dialog-heading">
                {heading}
              </h2>
              {subtitle && <p className="modal__subtitle">{subtitle}</p>}
              {suggestions && (
                <p className="modal__suggestions">{suggestions}</p>
              )}
              {children && <div className="modal__body">{children}</div>}
              <div className="modal__actions">
                {cancelButton && (
                  <Button size="medium" onClick={onClose} {...cancelButton} />
                )}
                {confirmButton && (
                  <Button
                    size="medium"
                    onClick={onConfirm}
                    {...confirmButton}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.getElementById("modal-root")
  );
};

Modal.propTypes = {
  variation: PropTypes.oneOf(["basic", "alert"]),
  iconName: PropTypes.string,
  iconTheme: PropTypes.string,
  className: PropTypes.string,
  heading: PropTypes.string,
  subtitle: PropTypes.string,
  suggestions: PropTypes.string,
  cancelButton: PropTypes.object,
  confirmButton: PropTypes.object,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

Modal.defaultProps = {
  variation: "basic",
  heading: "Delete All Rows?",
  cancelButton: {
    variant: "outline",
    children: "Cancel",
  },
  onClose: () => {},
  onConfirm: () => {},
};

export default Modal;
